import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';


const UserActivities = () => {
  const [userActivities, setUserActivities] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // 获取所有用户活动记录
      const userActivitiesResponse = await apiService.getAllUserActivities();
      
      // 获取所有用户
      const usersResponse = await apiService.getUsers();
      
      // 获取所有活动
      const activitiesResponse = await apiService.getActivities();
      
      setUserActivities(userActivitiesResponse.data);
      setUsers(usersResponse.data);
      setActivities(activitiesResponse.data);
    } catch (err) {
      setError('获取数据失败');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return '未知用户';
    return user.nickname || user.username || '未知用户';
  };

  const getActivityName = (activityId) => {
    const activity = activities.find(a => a.id === activityId);
    return activity ? activity.name : '未知活动';
  };

  const handleStatusChange = async (userActivityId, newStatus) => {
    try {
      await apiService.updateUserActivityStatus(userActivityId, newStatus);
      
      // 重新获取数据
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || '更新状态失败');
      console.error(err);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'registered':
        return <span className="badge bg-warning">已报名</span>;
      case 'attended':
        return <span className="badge bg-info">已参加</span>;
      case 'completed':
        return <span className="badge bg-success">已完成</span>;
      case 'deleted':
        return <span className="badge bg-danger">已取消</span>;
      default:
        return <span className="badge bg-secondary">未知</span>;
    }
  };


  // 搜索过滤
  const filteredUserActivities = userActivities.filter(ua => {
    const kw = search.trim().toLowerCase();
    if (!kw) return true;
    const user = users.find(u => u.id === ua.user_id);
    const activity = activities.find(a => a.id === ua.activity_id);
    return (
      (user && (
        (user.username && user.username.toLowerCase().includes(kw)) ||
        (user.nickname && user.nickname.toLowerCase().includes(kw))
      )) ||
      (activity && activity.name && activity.name.toLowerCase().includes(kw)) ||
      (ua.status && ua.status.toLowerCase().includes(kw))
    );
  });

  if (loading && userActivities.length === 0) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">加载中...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-4">活动报名管理</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      
      <div className="card">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h4 className="mb-0">报名记录列表</h4>
          <input
            type="text"
            className="form-control form-control-sm ms-2"
            style={{ maxWidth: 220 }}
            placeholder="关键字搜索..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="card-body">

          {userActivities.length === 0 ? (
            <div className="alert alert-info">暂无报名记录</div>
          ) : filteredUserActivities.length === 0 ? (
            <div className="alert alert-info">暂无匹配记录</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>用户</th>
                    <th>活动</th>
                    <th>状态</th>
                    <th>报名时间</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUserActivities.map(ua => (
                    <tr key={ua.id}>
                      <td>{ua.id}</td>
                      <td>{getUserName(ua.user_id)}</td>
                      <td>{getActivityName(ua.activity_id)}</td>
                      <td>{getStatusBadge(ua.status)}</td>
                      <td>{new Date(ua.registered_at).toLocaleString()}</td>
                      <td>
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => handleStatusChange(ua.id, 'registered')}
                            disabled={ua.status === 'registered'}
                          >
                            设为已报名
                          </button>
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => handleStatusChange(ua.id, 'attended')}
                            disabled={ua.status === 'attended'}
                          >
                            设为已参加
                          </button>
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={() => handleStatusChange(ua.id, 'completed')}
                            disabled={ua.status === 'completed'}
                          >
                            设为已完成
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger ms-2"
                            onClick={() => handleStatusChange(ua.id, 'deleted')}
                            disabled={ua.status === 'deleted'}
                          >
                            取消报名
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      <div className="alert alert-info mt-4">
        <h5>状态说明：</h5>
        <ul>
          <li><span className="badge bg-warning">已报名</span> - 用户已报名但尚未参加活动</li>
          <li><span className="badge bg-info">已参加</span> - 用户已参加活动但尚未完成</li>
          <li><span className="badge bg-success">已完成</span> - 用户已完成活动并获得积分</li>
        </ul>
        <ul>
          <li><span className="badge bg-danger">已取消</span> - 报名已被管理员取消，用户无法参与该活动。</li>
        </ul>
        <p className="mb-0">
          <strong>注意：</strong> 当状态变更为"已完成"时，系统会自动为用户添加相应的积分。
        </p>
      </div>
    </div>
  );
};

export default UserActivities;
